import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { authenticatedFetch, getAccessToken } from '../utils/auth.js'
import { writeAccountSnapshot } from '../utils/accountSnapshot.js'
import {
  countPlanCookProgress,
  remainingNightsInPlanWeek,
  planCookDayLabel,
} from '../utils/planCookProgress.js'
import { readGuestToken } from '../utils/guestKitchen.js'
import { notifySavedListsInvalidated } from '../savedListsCache.js'
import { readPlanWeekStart } from '../utils/appNavigation.js'
import { useToast } from './useToast.js'
import { trackCookingEvent, trackMealPlanItemMoved } from '../utils/cookingAnalytics.js'
import {
  packTelemetryIds,
  RECIPE_PACK_SURFACES,
  trackRecipePackEvent,
} from '../utils/recipePackAnalytics.js'
import { requestPantryCleanupAfterCook } from '../utils/pantryCleanup.js'
import { planWithCookPlacements, toPlanWithCookRequest } from '../utils/planWithCookPreview.js'
import {
  resolveCuratedPacks,
  deriveAddedPackIds,
} from '../components/RecipePacks/resolveCuratedPacks.js'
import {
  MEAL_PLAN_DAY_ORDER,
  derivePlannerState,
  THRESHOLDS,
  buildPlannerCalendarContext,
  isPastDayKey,
  dowFromDayKey,
  formatWeekLabelFromMonday,
  deriveLivePlannerState,
  deriveLivePlannerBanner,
  formatRecipeTimeMinutes,
  currentWeekStartIso,
  todayKeyInPlanWeek,
  onboardingPlanWeekStartIso,
  addWeeksToMonday,
  isFutureWeek,
  maxPlannerViewWeekStartIso,
  plannerHeroTitleForWeek,
  patchDerivedFromPlanItems,
  patchDerivedSuggestionsFromApi,
  cookReadyFromSavedList,
  buildExtendedPlannerStripDays,
  mealMoveNeedsConflictConfirm,
  weekHasGeneratedGrocery,
  plannerGroceryRecipeSignature,
} from '../utils/mealPlanHelpers.js'

const MS_PER_DAY = 86400000

function parsePlannerFetchError(text, fallback) {
  if (!text) return fallback
  try {
    const data = JSON.parse(text)
    return data.message || data.detail || fallback
  } catch {
    return text.length > 200 ? `${text.slice(0, 200)}…` : text
  }
}

/** When user dismisses the Plan "Light pantry" banner, hide until `pantryCount` changes. */
const LIGHT_PANTRY_BANNER_DISMISSED_KEY = 'planner_light_pantry_banner_dismissed_for_count'

function readLightPantryDismissedPantryCount() {
  try {
    const raw = localStorage.getItem(LIGHT_PANTRY_BANNER_DISMISSED_KEY)
    if (raw == null || raw === '') return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

function writeLightPantryDismissedPantryCount(pantryCount) {
  try {
    localStorage.setItem(LIGHT_PANTRY_BANNER_DISMISSED_KEY, String(pantryCount))
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * True when a recipe's `last_cooked_date` falls inside the repeat window
 * (default 14 days, from THRESHOLDS.repeatDays). Drives the "Repeat" /
 * "Cooked recently" badge on assigned + suggestion meal cards.
 */
function wasCookedRecently(recipe) {
  const raw = recipe?.last_cooked_date
  if (!raw) return false
  const t = new Date(raw).getTime()
  if (!Number.isFinite(t)) return false
  return Date.now() - t <= THRESHOLDS.repeatDays * MS_PER_DAY
}

function ingredientLineFromSavedListItem(item) {
  if (!item) return null
  if (typeof item === 'string') {
    const n = item.trim()
    return n ? { name: n, category: 'staples' } : null
  }
  if (typeof item === 'object') {
    // GET /saved-lists/{id} uses GroceryItem: primary text is `item`, not `name`.
    const name = String(
      item.item ||
        item.name ||
        item.item_name ||
        item.ingredient ||
        item.ingredient_name ||
        ''
    ).trim()
    if (!name) return null
    const category = String(item.category || '').trim() || 'staples'
    return { name, category }
  }
  return null
}

function enrichMealsWithRecipeMeta(meals, recipeLists) {
  const byId = Object.fromEntries((recipeLists || []).map((r) => [String(r.id), r]))
  return meals.map((m) => {
    const r = m.recipeId ? byId[m.recipeId] : null
    if (!r) return m
    const hasItems = Array.isArray(r.items)
    const ingredientLines = hasItems
      ? r.items.map((item) => ingredientLineFromSavedListItem(item)).filter(Boolean)
      : []
    return {
      ...m,
      time: formatRecipeTimeMinutes(r.list_metadata || {}),
      repeat: wasCookedRecently(r),
      canCook: cookReadyFromSavedList(r),
      // Full lines load when the grocery rail opens the All filter.
      allIngredients: hasItems ? ingredientLines : m.allIngredients,
    }
  })
}

function mealPresentationKey(meals) {
  return (meals || []).map((meal) => {
    const lines = Array.isArray(meal.allIngredients)
      ? meal.allIngredients.map((line) => (
        typeof line === 'string' ? line : (line?.name || '')
      )).join(',')
      : ''
    return [
      meal.planItemId || meal.recipeId || '',
      meal.time || '',
      meal.repeat ? '1' : '0',
      meal.canCook ? '1' : '0',
      lines,
    ].join(':')
  }).join('|')
}

/** Keep ingredient bodies already fetched when a summary refresh arrives. */
function mergeRecipeListRows(previous, incoming) {
  const prevById = new Map((previous || []).map((row) => [String(row.id), row]))
  const next = (incoming || []).map((row) => {
    const old = prevById.get(String(row.id))
    if (old && Array.isArray(old.items) && !Array.isArray(row.items)) {
      return { ...row, items: old.items }
    }
    return row
  })
  const seen = new Set(next.map((row) => String(row.id)))
  for (const old of previous || []) {
    if (!seen.has(String(old.id)) && Array.isArray(old.items)) next.push(old)
  }
  return next
}

/**
 * GET /saved-lists/?recipes_only=… returns summaries without `items`.
 * Fetch full recipes for assigned dinners when the grocery All filter opens.
 * Returns `{ lists, failed }`.
 */
async function hydrateSavedListSummariesWithItemsForAssigned(summaries, assignedMeals) {
  const list = Array.isArray(summaries) ? summaries.map((row) => ({ ...row })) : []
  const byId = new Map(list.map((row) => [String(row.id), row]))
  const recipeIds = [
    ...new Set(
      (assignedMeals || []).map((meal) => meal.recipeId).filter(Boolean).map(String),
    ),
  ]
  let failed = false
  await Promise.all(
    recipeIds.map(async (id) => {
      const row = byId.get(id)
      if (row && Array.isArray(row.items)) return
      const res = await authenticatedFetch(`/saved-lists/${encodeURIComponent(id)}`)
      if (!res.ok) {
        failed = true
        return
      }
      const full = await res.json().catch(() => null)
      if (!full || full.id == null) {
        failed = true
        return
      }
      const merged = {
        ...(row || {}),
        ...full,
        items: Array.isArray(full.items) ? full.items : [],
      }
      byId.set(id, merged)
      if (!row) list.push(merged)
    }),
  )
  return {
    lists: list.map((row) => byId.get(String(row.id)) || row),
    failed,
  }
}

async function fetchPlannerPayload(weekStart) {
  const qs = new URLSearchParams({
    week_start: weekStart,
    min_count: '3',
    max_count: '5',
  })
  const res = await authenticatedFetch(`/meal-plans/planner?${qs}`)
  if (!res.ok) {
    const t = await res.text().catch(() => '')
    throw new Error(t || `Planner ${res.status}`)
  }
  const body = await res.json().catch(() => null)
  if (!body?.plan?.id) {
    throw new Error('Invalid planner response')
  }
  return {
    plan: body.plan,
    suggestions: Array.isArray(body.suggestions) ? body.suggestions : [],
  }
}

function suggestionRowsToApiShape(rows) {
  return {
    ok: true,
    rows: (rows || []).map((r) => ({
      recipe_id: r.recipe_id,
      name: r.name,
      matched_count: r.matched_count,
      total_count: r.total_count,
      bucket: r.bucket,
      weighted_match: r.weighted_match,
      final_score: r.final_score,
      source: r.source,
      why: r.why,
      missing_other: r.missing_other,
      total_time: r.total_time,
    })),
  }
}

function buildDerivedFromPlannerPayload(plan, suggestionRows, lists) {
  const derived = deriveLivePlannerState(
    plan,
    lists,
    suggestionRowsToApiShape(suggestionRows),
  )
  derived.assigned = enrichMealsWithRecipeMeta(derived.assigned, lists)
  derived.suggestions = enrichMealsWithRecipeMeta(derived.suggestions, lists)
  return derived
}

function assignedNeedsIngredientLines(meals) {
  return (meals || []).some((meal) => meal.recipeId && !Array.isArray(meal.allIngredients))
}

/**
 * @param {{ onSwitchToShop?: () => void }} [opts]
 *   onSwitchToShop — called after missing items are appended to Current Shopping List
 *   so the host can open Shop; list sync uses the `pending_chat_saved_list_id` handshake.
 */
export function usePlannerState({ onSwitchToShop, onboardingMode = false } = {}) {
  const { showToast } = useToast()
  const [libraryCount, setLibraryCount] = useState(null)
  const [pantryCount, setPantryCount] = useState(null)
  const [lightPantryBannerDismissedForCount, setLightPantryBannerDismissedForCount] = useState(
    readLightPantryDismissedPantryCount,
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [assignedIngredientsLoading, setAssignedIngredientsLoading] = useState(false)
  const [assignedIngredientsError, setAssignedIngredientsError] = useState(false)
  const [preferences, setPreferences] = useState({})

  const [useLivePlanner, setUseLivePlanner] = useState(false)
  const [livePlan, setLivePlan] = useState(null)
  const [recipeLists, setRecipeLists] = useState([])
  const recipeListsRef = useRef([])
  const [planBusy, setPlanBusy] = useState(false)
  const [planNotice, setPlanNotice] = useState(null)
  const [pendingMoveConflict, setPendingMoveConflict] = useState(null)
  const [starterPacks, setStarterPacks] = useState([])
  const [libraryReloadToken, setLibraryReloadToken] = useState(0)
  const [assistantEvents, setAssistantEvents] = useState([])
  const [assistantBusy, setAssistantBusy] = useState(false)
  const [assistantPendingConfirmation, setAssistantPendingConfirmation] = useState(null)
  const [assistantPendingClarification, setAssistantPendingClarification] = useState(null)
  const [assistantActiveDays, setAssistantActiveDays] = useState([])
  const [planCookFlow, setPlanCookFlow] = useState({
    status: 'idle',
    error: '',
    draft: null,
  })
  const planCookJumpRef = useRef(false)
  const planCookWeekRef = useRef(null)
  const [viewedWeekStart, setViewedWeekStart] = useState(() => {
    const current = currentWeekStartIso()
    const maxView = maxPlannerViewWeekStartIso()
    if (onboardingMode) {
      const start = onboardingPlanWeekStartIso()
      if (start >= current && start <= maxView) return start
    }
    const fromUrl = readPlanWeekStart()
    if (fromUrl && fromUrl >= current && fromUrl <= maxView) return fromUrl
    return current
  })
  const viewedWeekStartRef = useRef(viewedWeekStart)
  const [weekNavLoading, setWeekNavLoading] = useState(false)
  const [forwardWeekPlan, setForwardWeekPlan] = useState(null)
  const skipWeekNavRefetchRef = useRef(true)
  const weekCacheRef = useRef(new Map())
  const planOpenedTrackedRef = useRef(false)
  const livePlanRef = useRef(null)
  const ingredientFetchRef = useRef(null)

  const invalidateWeekCache = useCallback(() => {
    weekCacheRef.current.clear()
  }, [])

  useEffect(() => {
    recipeListsRef.current = recipeLists
  }, [recipeLists])

  useEffect(() => {
    livePlanRef.current = livePlan
  }, [livePlan])

  useEffect(() => {
    setAssignedIngredientsError(false)
    ingredientFetchRef.current = null
  }, [viewedWeekStart])

  useEffect(() => {
    const current = livePlanRef.current
    if (!current) return
    const assigned = enrichMealsWithRecipeMeta(current.assigned, recipeLists)
    const suggestions = enrichMealsWithRecipeMeta(current.suggestions, recipeLists)
    if (
      mealPresentationKey(assigned) === mealPresentationKey(current.assigned)
      && mealPresentationKey(suggestions) === mealPresentationKey(current.suggestions)
    ) {
      return
    }
    const next = { ...current, assigned, suggestions }
    if (current.weekStartDate) weekCacheRef.current.set(current.weekStartDate, next)
    livePlanRef.current = next
    setLivePlan(next)
  }, [recipeLists, livePlan])

  useEffect(() => {
    viewedWeekStartRef.current = viewedWeekStart
  }, [viewedWeekStart])

  useEffect(() => {
    if (planCookWeekRef.current == null) {
      planCookWeekRef.current = viewedWeekStart
      return
    }
    if (planCookWeekRef.current === viewedWeekStart) return
    planCookWeekRef.current = viewedWeekStart
    if (planCookJumpRef.current) {
      planCookJumpRef.current = false
      return
    }
    setPlanCookFlow({ status: 'idle', error: '', draft: null })
  }, [viewedWeekStart])

  useEffect(() => {
    if (!livePlan?.planId || planOpenedTrackedRef.current) return
    planOpenedTrackedRef.current = true
    trackCookingEvent('meal_plan_opened', {
      plan_id: livePlan.planId,
      week_start: livePlan.weekStartDate,
      source: 'meal_plan',
    })
  }, [livePlan?.planId, livePlan?.weekStartDate])

  const refetchLivePlan = useCallback(
    async (weekStartOverride = null, { skipCache = false } = {}) => {
      const weekStart =
        weekStartOverride || viewedWeekStartRef.current || currentWeekStartIso()
      const lists = recipeListsRef.current

        if (!skipCache && weekCacheRef.current.has(weekStart)) {
        const cached = weekCacheRef.current.get(weekStart)
        if (weekStart === viewedWeekStartRef.current) {
          livePlanRef.current = cached
          setLivePlan(cached)
        }
        return cached
      }

      const { plan, suggestions } = await fetchPlannerPayload(weekStart)
      const derived = buildDerivedFromPlannerPayload(plan, suggestions, lists)
      weekCacheRef.current.set(weekStart, derived)
      if (weekStart === viewedWeekStartRef.current) {
        livePlanRef.current = derived
        setLivePlan(derived)
      }
      if (weekStart === currentWeekStartIso()) {
        window.dispatchEvent(
          new CustomEvent('mealPlanCurrentUpdated', { detail: { plan } }),
        )
      }
      return derived
    },
    [],
  )

  useEffect(() => {
    let cancelled = false

    const safeJson = async (res) => {
      if (!res || !res.ok) return null
      try {
        return await res.json()
      } catch {
        return null
      }
    }

    const token = getAccessToken()
    const canLoadPlanner = Boolean(token || readGuestToken())

    const extrasPromise = Promise.all([
      authenticatedFetch('/saved-lists/?recipes_only=true&limit=200').catch(() => null),
      authenticatedFetch('/inventory').catch(() => null),
      authenticatedFetch('/preferences/me').catch(() => null),
      token ? authenticatedFetch('/curated-packs').catch(() => null) : Promise.resolve(null),
    ]).then(async ([listsRes, inventoryRes, prefsRes, packsRes]) => {
      if (cancelled) return
      const listsJson = await safeJson(listsRes)
      const inventoryJson = await safeJson(inventoryRes)
      const prefsData = await safeJson(prefsRes)
      const packsJson = await safeJson(packsRes)
      if (cancelled) return

      const incoming = Array.isArray(listsJson) ? listsJson : []
      const lists = mergeRecipeListRows(recipeListsRef.current, incoming)
      recipeListsRef.current = lists
      setRecipeLists(lists)
      setLibraryCount(incoming.length)
      setPantryCount(Array.isArray(inventoryJson) ? inventoryJson.length : 0)
      setPreferences(prefsData?.preferences ?? {})
      setStarterPacks(resolveCuratedPacks(packsJson))
    })

    async function loadVisiblePlan() {
      const initialWeek = viewedWeekStartRef.current || currentWeekStartIso()
      const { plan, suggestions } = await fetchPlannerPayload(initialWeek)
      if (cancelled) return
      const derived = buildDerivedFromPlannerPayload(
        plan,
        suggestions,
        recipeListsRef.current,
      )
      if (cancelled) return
      weekCacheRef.current.set(initialWeek, derived)
      livePlanRef.current = derived
      setLivePlan(derived)
      setUseLivePlanner(true)
      setError(null)
      if (initialWeek === currentWeekStartIso()) {
        window.dispatchEvent(
          new CustomEvent('mealPlanCurrentUpdated', { detail: { plan } }),
        )
      }
    }

    async function load() {
      try {
        if (!canLoadPlanner) {
          await extrasPromise
          if (cancelled) return
          setUseLivePlanner(false)
          setLivePlan(null)
          livePlanRef.current = null
          setError(new Error('Live planner requires authenticated recipe access'))
          return
        }
        try {
          await loadVisiblePlan()
        } catch (mapErr) {
          console.warn('Live planner mapping failed', mapErr)
          await new Promise((resolve) => window.setTimeout(resolve, 400))
          if (cancelled) return
          await loadVisiblePlan()
        }
      } catch (retryErr) {
        if (!cancelled) {
          console.warn('Live planner retry failed', retryErr)
          setUseLivePlanner(false)
          setLivePlan(null)
          livePlanRef.current = null
          setError(
            retryErr instanceof Error ? retryErr : new Error('Live planner mapping failed'),
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    void extrasPromise.catch((err) => {
      console.warn('Planner extras failed', err)
    })
    return () => {
      cancelled = true
    }
  }, [libraryReloadToken, invalidateWeekCache])

  useEffect(() => {
    invalidateWeekCache()
  }, [libraryReloadToken, invalidateWeekCache])

  useEffect(() => {
    if (!useLivePlanner) return undefined
    const onPantryChanged = () => {
      invalidateWeekCache()
      void refetchLivePlan(null, { skipCache: true })
    }
    window.addEventListener('pantry-inventory-changed', onPantryChanged)
    return () => window.removeEventListener('pantry-inventory-changed', onPantryChanged)
  }, [useLivePlanner, refetchLivePlan, invalidateWeekCache])

  useEffect(() => {
    if (!useLivePlanner) return undefined
    const onRefresh = () => {
      invalidateWeekCache()
      void refetchLivePlan(null, { skipCache: true })
    }
    window.addEventListener('mealPlanSuggestionsRefresh', onRefresh)
    return () => window.removeEventListener('mealPlanSuggestionsRefresh', onRefresh)
  }, [useLivePlanner, refetchLivePlan, invalidateWeekCache])

  useEffect(() => {
    if (!useLivePlanner) return undefined
    if (skipWeekNavRefetchRef.current) {
      skipWeekNavRefetchRef.current = false
      return undefined
    }
    const cached = weekCacheRef.current.get(viewedWeekStart)
    if (cached) {
      livePlanRef.current = cached
      setLivePlan(cached)
    }
    let cancelled = false
    setWeekNavLoading(true)
    refetchLivePlan(viewedWeekStart, { skipCache: Boolean(cached) })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
        }
      })
      .finally(() => {
        if (!cancelled) setWeekNavLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [viewedWeekStart, useLivePlanner, refetchLivePlan])

  const weekNav = useMemo(() => {
    const current = currentWeekStartIso()
    const maxView = maxPlannerViewWeekStartIso()
    const viewingCurrent = viewedWeekStart === current
    const canGoNext = viewedWeekStart < maxView
    const canGoPrevious = viewedWeekStart > current

    return {
      viewedWeekStart,
      isCurrentWeek: viewingCurrent,
      isFutureWeek: isFutureWeek(viewedWeekStart),
      canGoNext,
      canGoPrevious,
      showPlanNextWeek: viewingCurrent && canGoNext,
      loading: weekNavLoading,
      heroTitle: (isCold) => plannerHeroTitleForWeek(viewedWeekStart, isCold),
      goToCurrentWeek: () => setViewedWeekStart(current),
      goToNextWeek: () => {
        if (canGoNext) setViewedWeekStart(addWeeksToMonday(viewedWeekStart, 1))
      },
      goToPreviousWeek: () => {
        if (canGoPrevious) setViewedWeekStart(addWeeksToMonday(viewedWeekStart, -1))
      },
      goToPlanNextWeek: () => {
        if (viewingCurrent && canGoNext) {
          setViewedWeekStart(addWeeksToMonday(current, 1))
        }
      },
    }
  }, [viewedWeekStart, weekNavLoading])

  const weekLabel = useMemo(() => {
    if (viewedWeekStart) {
      return formatWeekLabelFromMonday(viewedWeekStart)
    }
    if (useLivePlanner && livePlan?.weekStartDate) {
      return formatWeekLabelFromMonday(livePlan.weekStartDate)
    }
    const now = new Date()
    const dow = now.getDay()
    const monday = new Date(now)
    monday.setDate(now.getDate() - ((dow + 6) % 7))
    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const getISOWeek = (date) => {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
      const dayNum = d.getUTCDay() || 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum)
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
      return Math.ceil((((d - yearStart) / 86400000) + 1) / 7)
    }
    const fmt = (d) => `${months[d.getMonth()]} ${d.getDate()}`
    return `Week ${getISOWeek(now)} · ${fmt(monday)} – ${fmt(sunday)}`
  }, [useLivePlanner, livePlan, viewedWeekStart])

  // Persist last-week progress so logged-out returning Home can greet them.
  useEffect(() => {
    if (!getAccessToken()) return
    if (!useLivePlanner || !livePlan?.weekStartDate) return
    const current = currentWeekStartIso()
    if (livePlan.weekStartDate !== current) return
    const { planned, cooked } = countPlanCookProgress(livePlan.assigned)
    const todayKey = todayKeyInPlanWeek(current)
    const tonight = (livePlan.assigned || []).find((meal) => (
      meal.assignedTo === todayKey
      && meal.recipeId
      && meal.status !== 'skipped'
      && meal.status !== 'night_off'
    ))
    writeAccountSnapshot({
      weekLabel,
      planned,
      cooked,
      remainingNights: remainingNightsInPlanWeek(current),
      dayLabel: planCookDayLabel(current) || '',
      tonightTitle: tonight?.name || null,
    })
  }, [useLivePlanner, livePlan, weekLabel])

  const calendarContext = useMemo(() => {
    if (!useLivePlanner || !livePlan?.weekStartDate) return null
    return buildPlannerCalendarContext(livePlan.weekStartDate)
  }, [useLivePlanner, livePlan])

  useEffect(() => {
    if (!useLivePlanner || !livePlan?.weekStartDate) {
      setForwardWeekPlan(null)
      return undefined
    }
    const nextStart = addWeeksToMonday(livePlan.weekStartDate, 1)
    if (nextStart > maxPlannerViewWeekStartIso()) {
      setForwardWeekPlan(null)
      return undefined
    }
    let cancelled = false
    const cached = weekCacheRef.current.get(nextStart)
    if (cached) setForwardWeekPlan(cached)
    refetchLivePlan(nextStart, { skipCache: Boolean(cached) })
      .then((derived) => {
        if (!cancelled && derived) setForwardWeekPlan(derived)
      })
      .catch(() => {
        if (!cancelled) setForwardWeekPlan(null)
      })
    return () => {
      cancelled = true
    }
  }, [useLivePlanner, livePlan?.weekStartDate, livePlan?.planId, refetchLivePlan])

  const putPlanItemRecipeOnPlan = useCallback(async (planId, planItemId, recipeId) => {
    if (!planId || !planItemId) return null
    const res = await authenticatedFetch(`/meal-plans/${planId}/items/${planItemId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipe_id: recipeId || null }),
    })
    if (!res.ok) {
      const t = await res.text().catch(() => '')
      throw new Error(parsePlannerFetchError(t, 'Failed to update meal'))
    }
    const item = await res.json().catch(() => null)
    if (!item || item.id == null) {
      throw new Error('Invalid meal item response')
    }
    if (recipeId) {
      trackCookingEvent('meal_plan_item_replaced', {
        plan_id: planId,
        plan_item_id: planItemId,
        recipe_id: recipeId,
        source: 'meal_plan',
      })
    }
    return item
  }, [])

  const putPlanItemRecipe = useCallback(
    async (planItemId, recipeId) => {
      if (!livePlan?.planId) return null
      return putPlanItemRecipeOnPlan(livePlan.planId, planItemId, recipeId)
    },
    [livePlan?.planId, putPlanItemRecipeOnPlan],
  )

  const patchPlanItemStatus = useCallback(
    async (planItemId, status, nightOffType = null, planIdOverride = null) => {
      const planId = planIdOverride || livePlan?.planId
      if (!planId) return null
      const body = { status }
      if (status === 'night_off') body.night_off_type = nightOffType
      const res = await authenticatedFetch(
        `/meal-plans/${planId}/items/${planItemId}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      )
      if (!res.ok) {
        const t = await res.text().catch(() => '')
        throw new Error(parsePlannerFetchError(t, 'Failed to update meal status'))
      }
      const item = await res.json().catch(() => null)
      if (!item || item.id == null) {
        throw new Error('Invalid meal item response')
      }
      return item
    },
    [livePlan?.planId],
  )

  const enrichDerived = useCallback((patched) => {
    const lists = recipeListsRef.current
    return {
      ...patched,
      assigned: enrichMealsWithRecipeMeta(patched.assigned, lists),
      suggestions: enrichMealsWithRecipeMeta(patched.suggestions, lists),
    }
  }, [])

  const applyPlanItemUpdatesForWeek = useCallback(
    (weekStart, items) => {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (!weekStart || list.length === 0) return
      const cached = weekCacheRef.current.get(weekStart)
      const base = cached || (livePlan?.weekStartDate === weekStart ? livePlan : null)
      if (base) {
        const enriched = enrichDerived(patchDerivedFromPlanItems(base, list))
        weekCacheRef.current.set(weekStart, enriched)
        if (weekStart === viewedWeekStartRef.current) {
          setLivePlan(enriched)
        }
        if (forwardWeekPlan?.weekStartDate === weekStart) {
          setForwardWeekPlan(enriched)
        }
      }
      void refetchLivePlan(weekStart, { skipCache: true }).then((derived) => {
        if (!derived) return
        if (derived.weekStartDate === viewedWeekStartRef.current) return
        if (forwardWeekPlan?.weekStartDate === derived.weekStartDate ||
            derived.weekStartDate === addWeeksToMonday(viewedWeekStartRef.current, 1)) {
          setForwardWeekPlan(derived)
        }
      })
    },
    [livePlan, forwardWeekPlan?.weekStartDate, enrichDerived, refetchLivePlan],
  )

  const applyPlanItemUpdates = useCallback(
    (items) => {
      const weekStart = livePlan?.weekStartDate
      if (!weekStart) return
      applyPlanItemUpdatesForWeek(weekStart, items)
    },
    [livePlan?.weekStartDate, applyPlanItemUpdatesForWeek],
  )

  const stripDays = useMemo(
    () => buildExtendedPlannerStripDays(livePlan, forwardWeekPlan),
    [livePlan, forwardWeekPlan],
  )

  const generatePlan = useCallback(async () => {
    if (!livePlan?.weekStartDate) {
      console.warn('[meal-plan] generatePlan skipped: no weekStartDate', { livePlan })
      setPlanNotice({ kind: 'error', text: 'No active plan week — try reloading the page.' })
      return
    }
    console.log('[meal-plan] generatePlan POST', { week_start: livePlan.weekStartDate })
    setPlanBusy(true)
    setPlanNotice({ kind: 'info', text: 'Auto-filling week…' })
    try {
      const res = await authenticatedFetch('/meal-plans/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          week_start: livePlan.weekStartDate,
          replace_existing: true,
        }),
      })
      console.log('[meal-plan] generatePlan response', res.status, res.statusText)
      if (!res.ok) {
        const t = await res.text().catch(() => '')
        console.error('[meal-plan] generatePlan failed', res.status, t)
        setPlanNotice({
          kind: 'error',
          text: `Auto-fill failed (${res.status}): ${t.slice(0, 160) || res.statusText}`,
        })
        return
      }
      invalidateWeekCache()
      await refetchLivePlan(null, { skipCache: true })
      trackCookingEvent('meal_plan_created', {
        plan_id: livePlan.planId,
        week_start: livePlan.weekStartDate,
        source: 'meal_plan',
        generation_mode: 'auto_fill',
      })
      setPlanNotice({ kind: 'success', text: 'Plan generated.' })
    } catch (err) {
      console.error('[meal-plan] generatePlan threw', err)
      setPlanNotice({ kind: 'error', text: `Auto-fill failed: ${err?.message || err}` })
    } finally {
      setPlanBusy(false)
    }
  }, [livePlan, refetchLivePlan, invalidateWeekCache])

  const isSlotPast = useCallback(
    (dayKey) => {
      if (!livePlan?.weekStartDate) return false
      const slot = livePlan.slotsByDay?.[dayKey]
      if (slot?.isPast) return true
      return isPastDayKey(livePlan.weekStartDate, dayKey)
    },
    [livePlan],
  )

  const assignToNextOpen = useCallback(
    async (recipeId) => {
      if (!livePlan?.slotsByDay) return
      const day = MEAL_PLAN_DAY_ORDER.find((d) => {
        const s = livePlan.slotsByDay[d]
        if (isSlotPast(d)) return false
        return s?.planItemId && !s.recipeId
      })
      if (!day) return
      const slot = livePlan.slotsByDay[day]
      setPlanBusy(true)
      try {
        const item = await putPlanItemRecipe(slot.planItemId, recipeId)
        if (item) {
          applyPlanItemUpdates([item])
          trackCookingEvent('meal_plan_item_added', {
            plan_id: livePlan.planId,
            plan_item_id: slot.planItemId,
            recipe_id: recipeId,
            source: 'meal_plan',
          })
        }
      } catch (err) {
        setPlanNotice({
          kind: 'error',
          text: err?.message || 'Could not add meal to the plan.',
        })
      } finally {
        setPlanBusy(false)
      }
    },
    [livePlan, putPlanItemRecipe, applyPlanItemUpdates, isSlotPast],
  )

  const maybePromptGroceryRefresh = useCallback((candidates) => {
    // candidates: Array<{ plan, beforeAssigned?, afterAssigned? }>
    // Prompt only when a week already pushed to Shop AND its grocery-contributing
    // recipes changed (day-only moves of the same meals are a no-op for Shop).
    const stale = (candidates || []).filter((c) => {
      if (!weekHasGeneratedGrocery(c?.plan)) return false
      if (c.beforeAssigned != null && c.afterAssigned != null) {
        return (
          plannerGroceryRecipeSignature(c.beforeAssigned)
          !== plannerGroceryRecipeSignature(c.afterAssigned)
        )
      }
      return true
    })
    if (stale.length === 0) return
    setPlanNotice({
      kind: 'info',
      text: 'Plan meals changed — add any new missing items to Shop?',
      actionLabel: 'Update Shop',
      actionKind: 'refresh_grocery',
    })
  }, [])

  const assignToDay = useCallback(
    async (movingMeal, targetDayKey, options = {}) => {
      if (!livePlan?.slotsByDay) return
      if (isSlotPast(targetDayKey)) return
      const target = livePlan.slotsByDay[targetDayKey]
      if (!target?.planItemId) return

      const stripDay = {
        dayKey: targetDayKey,
        weekStartDate: livePlan.weekStartDate,
        planId: livePlan.planId,
        planItemId: target.planItemId,
        isPast: Boolean(target.isPast),
        meal: (livePlan.assigned || []).find((m) => m.assignedTo === targetDayKey) || null,
      }
      if (
        !options.mode
        && mealMoveNeedsConflictConfirm(movingMeal, stripDay, livePlan.weekStartDate)
      ) {
        setPendingMoveConflict({
          movingMeal,
          stripDay,
          fromWeekStart: livePlan.weekStartDate,
        })
        return
      }

      setPlanBusy(true)
      try {
        const movingNightOff = movingMeal.status === 'night_off'
        const targetNightOff = stripDay.meal?.status === 'night_off'
        const updated = []
        const displacedRecipeId = stripDay.meal?.recipeId || null

        if (movingNightOff || (targetNightOff && movingMeal.assignedTo && movingMeal.recipeId)) {
          const sourceDay = movingMeal.assignedTo
          if (sourceDay === targetDayKey) return
          if (movingMeal.isPast || (sourceDay && isSlotPast(sourceDay))) {
            setPlanNotice({
              kind: 'error',
              text: 'That meal is on a past day and can’t be moved.',
            })
            return
          }
          const source = sourceDay ? livePlan.slotsByDay[sourceDay] : null
          if (sourceDay && !source?.planItemId) {
            setPlanNotice({
              kind: 'error',
              text: 'Could not move meal — source day is missing from the plan.',
            })
            return
          }
          if (movingNightOff) {
            const targetItem = await patchPlanItemStatus(
              target.planItemId,
              'night_off',
              movingMeal.nightOffType,
            )
            if (targetItem) updated.push(targetItem)
            if (source?.planItemId) {
              if (displacedRecipeId) {
                const sourceItem = await putPlanItemRecipe(source.planItemId, displacedRecipeId)
                if (sourceItem) updated.push(sourceItem)
              } else if (targetNightOff && stripDay.meal?.nightOffType) {
                const sourceItem = await patchPlanItemStatus(
                  source.planItemId,
                  'night_off',
                  stripDay.meal.nightOffType,
                )
                if (sourceItem) updated.push(sourceItem)
              } else {
                const sourceItem = await putPlanItemRecipe(source.planItemId, null)
                if (sourceItem) updated.push(sourceItem)
              }
            }
          } else {
            const placed = await putPlanItemRecipe(target.planItemId, movingMeal.recipeId)
            if (placed) updated.push(placed)
            const sourceNight = await patchPlanItemStatus(
              source.planItemId,
              'night_off',
              stripDay.meal.nightOffType,
            )
            if (sourceNight) updated.push(sourceNight)
          }
          trackMealPlanItemMoved({
            recipe_id: movingMeal.recipeId || null,
            from_plan_id: livePlan.planId,
            from_plan_item_id: source?.planItemId || null,
            from_week_start: livePlan.weekStartDate,
            from_day_key: sourceDay,
            to_plan_id: livePlan.planId,
            to_plan_item_id: target.planItemId,
            to_week_start: livePlan.weekStartDate,
            to_day_key: targetDayKey,
            cross_week: false,
            overwrite: Boolean(displacedRecipeId || targetNightOff),
            swapped: Boolean(displacedRecipeId || targetNightOff),
            displaced_recipe_id: displacedRecipeId,
            night_off_type: movingMeal.nightOffType || stripDay.meal?.nightOffType || null,
          })
          if (updated.length > 0) {
            const afterAssigned = patchDerivedFromPlanItems(livePlan, updated).assigned
            applyPlanItemUpdates(updated)
            maybePromptGroceryRefresh([{
              plan: livePlan,
              beforeAssigned: livePlan.assigned,
              afterAssigned,
            }])
          }
          return updated.length > 0
        }

        if (movingMeal.assignedTo) {
          const sourceDay = movingMeal.assignedTo
          if (sourceDay === targetDayKey) return
          if (movingMeal.isPast || isSlotPast(sourceDay)) {
            setPlanNotice({
              kind: 'error',
              text: 'That meal is on a past day and can’t be moved.',
            })
            return
          }
          const source = livePlan.slotsByDay[sourceDay]
          if (!source?.planItemId) {
            setPlanNotice({
              kind: 'error',
              text: 'Could not move meal — source day is missing from the plan.',
            })
            return
          }
          const tr = target.recipeId
          const sr = source.recipeId || movingMeal.recipeId || null
          if (!sr) {
            setPlanNotice({
              kind: 'error',
              text: 'Could not move meal — recipe is missing.',
            })
            return
          }
          const itemA = await putPlanItemRecipe(source.planItemId, tr || null)
          const itemB = await putPlanItemRecipe(target.planItemId, sr)
          if (itemA) updated.push(itemA)
          if (itemB) updated.push(itemB)
          trackMealPlanItemMoved({
            recipe_id: movingMeal.recipeId || sr,
            from_plan_id: livePlan.planId,
            from_plan_item_id: source.planItemId,
            from_week_start: livePlan.weekStartDate,
            from_day_key: sourceDay,
            to_plan_id: livePlan.planId,
            to_plan_item_id: target.planItemId,
            to_week_start: livePlan.weekStartDate,
            to_day_key: targetDayKey,
            cross_week: false,
            overwrite: false,
            swapped: true,
            displaced_recipe_id: displacedRecipeId,
          })
        } else if (movingMeal.recipeId) {
          const item = await putPlanItemRecipe(target.planItemId, movingMeal.recipeId)
          if (item) updated.push(item)
          trackMealPlanItemMoved({
            recipe_id: movingMeal.recipeId,
            from_plan_id: null,
            from_plan_item_id: null,
            from_week_start: null,
            from_day_key: null,
            to_plan_id: livePlan.planId,
            to_plan_item_id: target.planItemId,
            to_week_start: livePlan.weekStartDate,
            to_day_key: targetDayKey,
            cross_week: false,
            overwrite: Boolean(displacedRecipeId),
            swapped: false,
            displaced_recipe_id: displacedRecipeId,
          })
        }
        if (updated.length > 0) {
          const afterAssigned = patchDerivedFromPlanItems(livePlan, updated).assigned
          applyPlanItemUpdates(updated)
          maybePromptGroceryRefresh([{
            plan: livePlan,
            beforeAssigned: livePlan.assigned,
            afterAssigned,
          }])
        }
        return updated.length > 0
      } catch (err) {
        setPlanNotice({
          kind: 'error',
          text: err?.message || 'Could not move meal.',
        })
        return false
      } finally {
        setPlanBusy(false)
      }
    },
    [livePlan, putPlanItemRecipe, patchPlanItemStatus, applyPlanItemUpdates, isSlotPast, maybePromptGroceryRefresh],
  )

  /**
   * Assign (or move) a meal onto a calendar-strip day descriptor.
   * Supports dropping onto the prefetched next week without changing the viewed week first.
   * @param {object} movingMeal
   * @param {object} stripDay
   * @param {{ mode?: 'replace'|'swap' }} [options]
   */
  const assignToStripDay = useCallback(
    async (movingMeal, stripDay, options = {}) => {
      if (!movingMeal || !stripDay?.planId || !stripDay?.planItemId || stripDay.isPast) return

      const targetWeek = stripDay.weekStartDate
      const sameWeek = livePlan?.weekStartDate && targetWeek === livePlan.weekStartDate

      if (sameWeek) {
        await assignToDay(movingMeal, stripDay.dayKey, options)
        return
      }

      if (movingMeal.status === 'night_off') {
        if (
          !options.mode
          && mealMoveNeedsConflictConfirm(movingMeal, stripDay, livePlan?.weekStartDate)
        ) {
          setPendingMoveConflict({
            movingMeal,
            stripDay,
            fromWeekStart: livePlan?.weekStartDate || movingMeal.weekStartDate || null,
          })
          return
        }
        setPlanBusy(true)
        try {
          const updatesByWeek = new Map()
          const sourceWeek = livePlan?.weekStartDate || null
          const sourceDay = movingMeal.assignedTo || null
          const sourceSlot = sourceDay && livePlan?.slotsByDay
            ? livePlan.slotsByDay[sourceDay]
            : null
          const displacedRecipeId = stripDay.meal?.recipeId
            ? String(stripDay.meal.recipeId)
            : null
          const targetNightOff = stripDay.meal?.status === 'night_off'
          const mode = options.mode || 'replace'
          const placed = await patchPlanItemStatus(
            stripDay.planItemId,
            'night_off',
            movingMeal.nightOffType,
            stripDay.planId,
          )
          if (placed) {
            const list = updatesByWeek.get(targetWeek) || []
            list.push(placed)
            updatesByWeek.set(targetWeek, list)
          }
          if (sourceSlot?.planItemId && livePlan?.planId) {
            let vacated = null
            if (mode === 'swap' && displacedRecipeId) {
              vacated = await putPlanItemRecipeOnPlan(
                livePlan.planId,
                sourceSlot.planItemId,
                displacedRecipeId,
              )
            } else if (mode === 'swap' && targetNightOff && stripDay.meal?.nightOffType) {
              vacated = await patchPlanItemStatus(
                sourceSlot.planItemId,
                'night_off',
                stripDay.meal.nightOffType,
              )
            } else {
              vacated = await putPlanItemRecipeOnPlan(
                livePlan.planId,
                sourceSlot.planItemId,
                null,
              )
            }
            if (vacated) {
              const list = updatesByWeek.get(sourceWeek) || []
              list.push(vacated)
              updatesByWeek.set(sourceWeek, list)
            }
          }
          trackMealPlanItemMoved({
            recipe_id: null,
            from_plan_id: livePlan?.planId || null,
            from_plan_item_id: sourceSlot?.planItemId || null,
            from_week_start: sourceWeek,
            from_day_key: sourceDay,
            to_plan_id: stripDay.planId,
            to_plan_item_id: stripDay.planItemId,
            to_week_start: targetWeek,
            to_day_key: stripDay.dayKey,
            cross_week: true,
            overwrite: mode === 'replace' && Boolean(displacedRecipeId || targetNightOff),
            swapped: mode === 'swap',
            displaced_recipe_id: displacedRecipeId,
            night_off_type: movingMeal.nightOffType || null,
          })
          for (const [weekStart, items] of updatesByWeek) {
            applyPlanItemUpdatesForWeek(weekStart, items)
          }
        } catch (err) {
          setPlanNotice({
            kind: 'error',
            text: err?.message || 'Could not move night off.',
          })
        } finally {
          setPlanBusy(false)
        }
        return
      }

      if (!movingMeal.recipeId) return

      if (
        !options.mode
        && mealMoveNeedsConflictConfirm(movingMeal, stripDay, livePlan?.weekStartDate)
      ) {
        setPendingMoveConflict({
          movingMeal,
          stripDay,
          fromWeekStart: livePlan?.weekStartDate || movingMeal.weekStartDate || null,
        })
        return
      }

      const mode = options.mode || 'replace'
      const displacedRecipeId = stripDay.meal?.recipeId
        ? String(stripDay.meal.recipeId)
        : null

      setPlanBusy(true)
      try {
        const updatesByWeek = new Map()
        const sourceWeek = livePlan?.weekStartDate || null
        const sourceDay = movingMeal.assignedTo || null
        let sourceSlot = null

        if (sourceDay && livePlan?.slotsByDay) {
          if (movingMeal.isPast || isSlotPast(sourceDay)) return
          sourceSlot = livePlan.slotsByDay[sourceDay]
          if (!sourceSlot?.planItemId) return
        }

        if (mode === 'swap' && sourceSlot && displacedRecipeId) {
          const clearedOrSwapped = await putPlanItemRecipeOnPlan(
            livePlan.planId,
            sourceSlot.planItemId,
            displacedRecipeId,
          )
          if (clearedOrSwapped) {
            const list = updatesByWeek.get(sourceWeek) || []
            list.push(clearedOrSwapped)
            updatesByWeek.set(sourceWeek, list)
          }
        } else if (sourceSlot) {
          const cleared = await putPlanItemRecipeOnPlan(
            livePlan.planId,
            sourceSlot.planItemId,
            null,
          )
          if (cleared) {
            const list = updatesByWeek.get(sourceWeek) || []
            list.push(cleared)
            updatesByWeek.set(sourceWeek, list)
          }
        }

        const placed = await putPlanItemRecipeOnPlan(
          stripDay.planId,
          stripDay.planItemId,
          movingMeal.recipeId,
        )
        if (placed) {
          const list = updatesByWeek.get(targetWeek) || []
          list.push(placed)
          updatesByWeek.set(targetWeek, list)
        }

        trackMealPlanItemMoved({
          recipe_id: movingMeal.recipeId,
          from_plan_id: livePlan?.planId || null,
          from_plan_item_id: sourceSlot?.planItemId || null,
          from_week_start: sourceWeek,
          from_day_key: sourceDay,
          to_plan_id: stripDay.planId,
          to_plan_item_id: stripDay.planItemId,
          to_week_start: targetWeek,
          to_day_key: stripDay.dayKey,
          cross_week: true,
          overwrite: mode === 'replace' && Boolean(displacedRecipeId),
          swapped: mode === 'swap',
          displaced_recipe_id: displacedRecipeId,
        })

        const groceryCandidates = []
        for (const [weekStart, items] of updatesByWeek) {
          const plan =
            livePlan?.weekStartDate === weekStart
              ? livePlan
              : forwardWeekPlan?.weekStartDate === weekStart
                ? forwardWeekPlan
                : null
          if (plan) {
            groceryCandidates.push({
              plan,
              beforeAssigned: plan.assigned,
              afterAssigned: patchDerivedFromPlanItems(plan, items).assigned,
            })
          }
          applyPlanItemUpdatesForWeek(weekStart, items)
        }
        maybePromptGroceryRefresh(groceryCandidates)
      } catch (err) {
        setPlanNotice({
          kind: 'error',
          text: err?.message || 'Could not add meal to the plan.',
        })
      } finally {
        setPlanBusy(false)
      }
    },
    [
      livePlan,
      forwardWeekPlan,
      assignToDay,
      isSlotPast,
      putPlanItemRecipeOnPlan,
      patchPlanItemStatus,
      applyPlanItemUpdatesForWeek,
      maybePromptGroceryRefresh,
    ],
  )

  const resolveMoveConflict = useCallback(
    async (mode) => {
      const pending = pendingMoveConflict
      if (!pending?.movingMeal || !pending?.stripDay) return
      setPendingMoveConflict(null)
      await assignToStripDay(pending.movingMeal, pending.stripDay, { mode })
    },
    [pendingMoveConflict, assignToStripDay],
  )

  const cancelMoveConflict = useCallback(() => {
    setPendingMoveConflict(null)
  }, [])

  const unassignMeal = useCallback(
    async (meal) => {
      if (!meal?.planItemId) return
      if (meal.isPast) return
      setPlanBusy(true)
      try {
        const item = await putPlanItemRecipe(meal.planItemId, null)
        if (item) applyPlanItemUpdates([item])
      } finally {
        setPlanBusy(false)
      }
    },
    [putPlanItemRecipe, applyPlanItemUpdates],
  )

  /** In-place recipe swap on an assigned slot (day unchanged). */
  const swapRecipeInPlace = useCallback(
    async (meal, recipeId) => {
      if (!meal?.planItemId || !recipeId || meal.isPast) return
      setPlanBusy(true)
      try {
        const beforeAssigned = livePlan?.assigned
        const item = await putPlanItemRecipe(meal.planItemId, recipeId)
        if (item && livePlan) {
          const afterAssigned = patchDerivedFromPlanItems(livePlan, [item]).assigned
          applyPlanItemUpdates([item])
          maybePromptGroceryRefresh([{
            plan: livePlan,
            beforeAssigned,
            afterAssigned,
          }])
        } else if (item) {
          applyPlanItemUpdates([item])
        }
      } catch (err) {
        setPlanNotice({
          kind: 'error',
          text: err?.message || 'Could not swap recipe.',
        })
      } finally {
        setPlanBusy(false)
      }
    },
    [livePlan, putPlanItemRecipe, applyPlanItemUpdates, maybePromptGroceryRefresh],
  )

  const markMealCooked = useCallback(
    async (meal) => {
      if (!meal?.planItemId || !livePlan?.planId) return
      setPlanBusy(true)
      try {
        const res = await authenticatedFetch(
          `/meal-plans/${livePlan.planId}/items/${meal.planItemId}/cook`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          },
        )
        if (!res.ok) {
          const t = await res.text().catch(() => '')
          throw new Error(t || 'Failed to mark meal as cooked')
        }
        const item = await res.json().catch(() => null)
        if (item) applyPlanItemUpdates([item])
        else await refetchLivePlan(null, { skipCache: true })
        setPlanNotice(null)
        if (meal.recipeId) {
          await requestPantryCleanupAfterCook({
            recipeId: String(meal.recipeId),
            recipeTitle: meal.name || 'This meal',
          })
        }
      } catch (err) {
        setPlanNotice({
          kind: 'error',
          text: err?.message || 'Could not mark meal as cooked.',
        })
      } finally {
        setPlanBusy(false)
      }
    },
    [livePlan?.planId, refetchLivePlan, applyPlanItemUpdates],
  )

  const markMealSkipped = useCallback(
    async (meal) => {
      if (!meal?.planItemId) return
      setPlanBusy(true)
      try {
        const item = await patchPlanItemStatus(meal.planItemId, 'skipped')
        if (item) applyPlanItemUpdates([item])
        else await refetchLivePlan(null, { skipCache: true })
        trackCookingEvent('meal_plan_item_skipped', {
          plan_id: livePlan?.planId,
          plan_item_id: meal.planItemId,
          recipe_id: meal.recipeId,
          source: 'meal_plan',
        })
        setPlanNotice(null)
      } catch (err) {
        setPlanNotice({
          kind: 'error',
          text: err?.message || 'Could not skip meal.',
        })
      } finally {
        setPlanBusy(false)
      }
    },
    [livePlan?.planId, patchPlanItemStatus, refetchLivePlan, applyPlanItemUpdates],
  )

  const setNightOff = useCallback(
    async (mealOrDayKey, nightOffType) => {
      if (!nightOffType || !livePlan) return false
      const planItemId = typeof mealOrDayKey === 'string'
        ? livePlan.slotsByDay?.[mealOrDayKey]?.planItemId
        : mealOrDayKey?.planItemId
      if (!planItemId) return false
      setPlanBusy(true)
      try {
        const item = await patchPlanItemStatus(planItemId, 'night_off', nightOffType)
        if (item) applyPlanItemUpdates([item])
        else await refetchLivePlan(null, { skipCache: true })
        trackCookingEvent('meal_plan_item_night_off', {
          plan_id: livePlan.planId,
          plan_item_id: planItemId,
          night_off_type: nightOffType,
          source: 'meal_plan',
        })
        setPlanNotice(null)
        return true
      } catch (err) {
        setPlanNotice({
          kind: 'error',
          text: err?.message || 'Could not save night off.',
        })
        return false
      } finally {
        setPlanBusy(false)
      }
    },
    [livePlan, patchPlanItemStatus, applyPlanItemUpdates, refetchLivePlan],
  )

  const removePlannerCandidate = useCallback(
    async (recipeId, recipeName = '') => {
      if (!recipeId) return
      setPlanBusy(true)
      try {
        const res = await authenticatedFetch(
          `/meal-plans/candidate-recipes/${encodeURIComponent(recipeId)}`,
          { method: 'DELETE' },
        )
        if (!res.ok) {
          const raw = await res.text().catch(() => '')
          let msg = raw?.trim() || `Request failed (${res.status})`
          try {
            const j = JSON.parse(raw)
            if (typeof j?.detail === 'string') msg = j.detail
            else if (j?.detail != null) msg = String(j.detail)
          } catch {
            /* keep msg from raw */
          }
          throw new Error(msg)
        }
        const body = await res.json().catch(() => ({}))
        const suggestionRows = Array.isArray(body?.suggestions) ? body.suggestions : []
        setLivePlan((prev) => {
          if (!prev) return prev
          const planShim = {
            id: prev.planId,
            week_start_date: prev.weekStartDate,
            items: prev.assigned.map((m) => ({
              id: m.planItemId,
              recipe_id: m.recipeId,
              day_of_week: dowFromDayKey(m.assignedTo),
            })),
          }
          const next = patchDerivedSuggestionsFromApi(prev, suggestionRows, planShim)
          const lists = recipeListsRef.current
          return {
            ...next,
            suggestions: enrichMealsWithRecipeMeta(next.suggestions, lists),
          }
        })
        invalidateWeekCache()
        void refetchLivePlan(null, { skipCache: true })
        const label = String(recipeName || '').trim() || 'Recipe'
        showToast({
          message: `Removed “${label}” from Your Picks`,
          variant: 'success',
        })
      } catch (err) {
        showToast({
          message: err?.message || 'Could not remove from Your Picks',
          variant: 'error',
          duration: 7000,
        })
      } finally {
        setPlanBusy(false)
      }
    },
    [refetchLivePlan, showToast, invalidateWeekCache],
  )

  const buildGroceryList = useCallback(async ({ openShop = true, excludeItemNames = null } = {}) => {
    if (!livePlan?.planId) return null
    setPlanBusy(true)
    try {
      const body = {}
      if (Array.isArray(excludeItemNames) && excludeItemNames.length > 0) {
        body.exclude_item_names = excludeItemNames
      }
      const res = await authenticatedFetch(`/meal-plans/${livePlan.planId}/grocery-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const t = await res.text().catch(() => '')
        throw new Error(t || 'Could not add missing items to Shop')
      }
      const data = await res.json()
      const id = data.saved_list_id
      const addedCount = typeof data.added_count === 'number' ? data.added_count : null
      trackCookingEvent('meal_plan_grocery_generated', {
        plan_id: livePlan.planId,
        source: 'meal_plan',
        saved_list_id: id || null,
        added_count: addedCount,
      })
      setLivePlan((prev) => {
        if (!prev || prev.planId !== livePlan.planId) return prev
        return {
          ...prev,
          planMetadata: {
            ...(prev.planMetadata || {}),
            grocery_list_generated_at: new Date().toISOString(),
            grocery_list_id: id || prev.planMetadata?.grocery_list_id || null,
          },
        }
      })
      setPlanNotice(null)
      if (id) {
        const listId = String(id)
        notifySavedListsInvalidated()
        if (openShop) {
          // Stash so Shop can sync Current Shopping List even if the listener
          // isn't mounted yet (same handshake as chat → Shop).
          try {
            localStorage.setItem('pending_chat_saved_list_id', listId)
          } catch {
            // ignore quota / private-mode failures
          }
          window.dispatchEvent(
            new CustomEvent('loadSavedList', { detail: { listId } }),
          )
        }
        const toastMessage =
          addedCount === 0
            ? 'Already on Shop — Plan “Missing” clears when items are in Pantry'
            : addedCount != null
              ? `Added ${addedCount} missing item${addedCount === 1 ? '' : 's'} to Shop`
              : 'Missing items added to Shop'
        showToast({
          message: openShop && addedCount !== 0 ? `${toastMessage} — opening Shop` : toastMessage,
          variant: 'success',
        })
        if (openShop && typeof onSwitchToShop === 'function') {
          onSwitchToShop()
        }
      } else {
        showToast({
          message: 'Missing items added to Shop',
          variant: 'success',
        })
      }
      return data
    } catch (err) {
      const msg =
        err && typeof err.message === 'string' && err.message
          ? err.message
          : 'Could not add missing items to Shop.'
      showToast({ message: msg, variant: 'error' })
      throw err
    } finally {
      setPlanBusy(false)
    }
  }, [livePlan?.planId, onSwitchToShop, showToast])

  const clearAssistantConfirmation = useCallback(() => {
    setAssistantPendingConfirmation(null)
  }, [])

  const clearAssistantClarification = useCallback(() => {
    setAssistantPendingClarification(null)
  }, [])

  const runAssistantCommand = useCallback(
    async (message, { confirm = false, onFallback = null, planWithCook = null, draft = null } = {}) => {
      const trimmed = String(message || '').trim()
      if ((!trimmed && !planWithCook) || !livePlan?.planId) return
      const structured = Boolean(planWithCook)
      setAssistantBusy(true)
      setAssistantActiveDays([])
      setAssistantPendingClarification(null)
      if (structured) {
        setPlanCookFlow({ status: 'loading', error: '', draft })
      }
      setAssistantEvents((prev) => [...prev.slice(-20), { type: 'message', payload: { message: trimmed || 'Plan with Cook' } }])
      if (confirm) {
        setAssistantPendingConfirmation(null)
      }
      let structuredFailed = false
      let planPatched = false
      try {
        const res = await authenticatedFetch('/meal-plans/assistant/stream', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(structured
            ? {
              message: 'Plan with Cook',
              plan_id: livePlan.planId,
              confirm: true,
              plan_with_cook: planWithCook,
            }
            : { message: trimmed, plan_id: livePlan.planId, confirm: !!confirm }),
        })
        if (!res.ok || !res.body) {
          const t = await res.text().catch(() => '')
          throw new Error(t || `Planner assistant failed (${res.status})`)
        }

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''
          for (const rawLine of lines) {
            const line = rawLine.trim()
            if (!line) continue
            let evt = null
            try {
              evt = JSON.parse(line)
            } catch {
              continue
            }
            setAssistantEvents((prev) => [...prev.slice(-20), evt])
            if (evt?.type === 'plan_patch') {
              planPatched = true
              const days = Array.isArray(evt?.payload?.days)
                ? evt.payload.days.filter((d) => Number.isInteger(d))
                : []
              setAssistantActiveDays(days)
              // Assistant just mutated the plan — never serve the pre-command cache.
              invalidateWeekCache()
              const patchWeek = evt?.payload?.week_start_date
              if (
                typeof patchWeek === 'string'
                && /^\d{4}-\d{2}-\d{2}$/.test(patchWeek)
                && patchWeek !== viewedWeekStartRef.current
              ) {
                if (structured) planCookJumpRef.current = true
                // e.g. "Plan next week" from the current-week panel — jump to that week.
                setViewedWeekStart(patchWeek)
              } else {
                await refetchLivePlan(null, { skipCache: true })
              }
            } else if (evt?.type === 'needs_confirmation') {
              setAssistantPendingConfirmation({
                message: evt?.payload?.message || 'Confirm this plan update?',
                messageText: trimmed,
                command: evt?.payload?.command || null,
                suggested_actions: Array.isArray(evt?.payload?.suggested_actions)
                  ? evt.payload.suggested_actions
                  : null,
              })
            } else if (evt?.type === 'needs_clarification') {
              if (structured) {
                structuredFailed = true
                setPlanCookFlow({
                  status: 'error',
                  error: evt?.payload?.message || 'Couldn\u2019t plan the week right now. Try again.',
                  draft,
                })
              }
              setAssistantPendingClarification({
                message: evt?.payload?.message || 'Need a bit more detail.',
                suggested_actions: Array.isArray(evt?.payload?.suggested_actions)
                  ? evt.payload.suggested_actions
                  : null,
              })
            } else if (evt?.type === 'done') {
              if (structured && !structuredFailed) {
                const summary = String(evt?.payload?.summary || '')
                if (/no open nights|already has dinners|could not|failed/i.test(summary)) {
                  setPlanCookFlow({ status: 'error', error: summary, draft })
                } else {
                  setPlanCookFlow({ status: 'done', error: '', draft })
                }
              }
              const followUps = Array.isArray(evt?.payload?.suggested_actions)
                ? evt.payload.suggested_actions
                : null
              if (followUps && followUps.length > 0 && !structured) {
                setAssistantPendingClarification({
                  message: evt?.payload?.summary || 'Next step?',
                  suggested_actions: followUps,
                })
              }
            } else if (evt?.type === 'fallback_to_chat') {
              if (structured) {
                structuredFailed = true
                setPlanCookFlow({
                  status: 'error',
                  error: 'Couldn\u2019t plan the week right now. Try again.',
                  draft,
                })
              } else if (typeof onFallback === 'function') {
                onFallback(trimmed)
              }
            } else if (evt?.type === 'error' && structured) {
              structuredFailed = true
              setPlanCookFlow({
                status: 'error',
                error: 'Couldn\u2019t plan the week right now. Try again.',
                draft,
              })
            }
          }
        }
        return !structured || (planPatched && !structuredFailed)
      } catch (err) {
        const msg = err?.message || 'Planner assistant failed.'
        if (structured) {
          setPlanCookFlow({
            status: 'error',
            error: 'Couldn\u2019t plan the week right now. Try again.',
            draft,
          })
        } else {
          showToast({ message: msg, variant: 'error' })
        }
        setAssistantEvents((prev) => [...prev.slice(-20), { type: 'error', payload: { message: msg } }])
        return false
      } finally {
        setAssistantBusy(false)
      }
    },
    [livePlan?.planId, refetchLivePlan, invalidateWeekCache, showToast, setViewedWeekStart],
  )

  const runPlanWithCook = useCallback(async (draft) => {
    const guestKitchen = !getAccessToken() && Boolean(readGuestToken())
    if (!guestKitchen) {
      return runAssistantCommand('Plan with Cook', {
        planWithCook: toPlanWithCookRequest(draft),
        draft,
      })
    }
    if (!livePlan?.planId) return false
    setPlanCookFlow({ status: 'loading', error: '', draft })
    try {
      const { dinners, nightsOff } = planWithCookPlacements(draft, draft?.plannedDayKeys || [])
      const slots = livePlan.slotsByDay || {}
      if (!dinners.length) {
        throw new Error('No dinners to plan')
      }
      for (const dinner of dinners) {
        const planItemId = slots[dinner.dayKey]?.planItemId
        if (!planItemId) throw new Error(`Missing ${dinner.dayKey} slot`)
        await putPlanItemRecipeOnPlan(livePlan.planId, planItemId, dinner.recipeId)
      }
      for (const night of nightsOff) {
        const planItemId = slots[night.dayKey]?.planItemId
        if (!planItemId) continue
        await patchPlanItemStatus(planItemId, 'night_off', night.nightOffType, livePlan.planId)
      }
      invalidateWeekCache()
      await refetchLivePlan(null, { skipCache: true })
      setPlanCookFlow({ status: 'done', error: '', draft })
      return true
    } catch {
      setPlanCookFlow({
        status: 'error',
        error: 'Couldn\u2019t plan the week right now. Try again.',
        draft,
      })
      return false
    }
  }, [
    livePlan?.planId,
    livePlan?.slotsByDay,
    putPlanItemRecipeOnPlan,
    patchPlanItemStatus,
    invalidateWeekCache,
    refetchLivePlan,
    runAssistantCommand,
  ])

  const addedPackIds = useMemo(
    () => deriveAddedPackIds(recipeLists, starterPacks),
    [recipeLists, starterPacks],
  )

  const plannerState =
    libraryCount != null && pantryCount != null
      ? derivePlannerState({ libraryCount, pantryCount })
      : 'healthy'

  const livePlannerBanner = useMemo(() => {
    if (!useLivePlanner || !livePlan) return null
    const raw = deriveLivePlannerBanner({
      plannerState,
      libraryCount,
      pantryCount,
      assignedMeals: livePlan.assigned,
      recipeLists,
    })
    if (!raw) return null
    if (
      raw.dismissible &&
      pantryCount != null &&
      lightPantryBannerDismissedForCount === pantryCount
    ) {
      return null
    }
    return raw
  }, [
    useLivePlanner,
    livePlan,
    plannerState,
    libraryCount,
    pantryCount,
    recipeLists,
    lightPantryBannerDismissedForCount,
  ])

  const dismissLivePlannerBanner = useCallback(() => {
    if (pantryCount == null) return
    setLightPantryBannerDismissedForCount(pantryCount)
    writeLightPantryDismissedPantryCount(pantryCount)
  }, [pantryCount])

  const onApplyStarterPack = useCallback(
    async (pack) => {
      const slug = pack?.slug || pack?.id
      if (!slug) return
      const surface =
        plannerState === 'cold-start'
          ? RECIPE_PACK_SURFACES.PLANNER_COLD_START
          : RECIPE_PACK_SURFACES.PLANNER_THIN_LIBRARY
      const packKeys = [pack.id, pack.slug, slug].filter(Boolean)
      const alreadyAdded = packKeys.some((key) => addedPackIds.has(key))
      const ids = packTelemetryIds(pack)

      trackRecipePackEvent('recipe_pack_apply_clicked', {
        surface,
        ...ids,
        already_added: alreadyAdded,
      })

      setPlanBusy(true)
      let applyFailureTracked = false
      try {
        const res = await authenticatedFetch(
          `/curated-packs/${encodeURIComponent(slug)}/apply`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          },
        )
        const body = await res.json().catch(() => ({}))
        if (!res.ok) {
          const detail = body?.detail
          const msg =
            typeof detail === 'string'
              ? detail
              : Array.isArray(detail)
                ? detail.map((d) => d.msg || d).join(', ')
                : `Could not add pack (${res.status})`
          trackRecipePackEvent('recipe_pack_apply_failed', {
            surface,
            ...ids,
            error_code: res.status,
          })
          applyFailureTracked = true
          showToast({ message: msg, variant: 'error' })
          return
        }
        const n = body.created_count ?? 0
        const refreshed = body.refreshed_count ?? 0
        const skipped = body.skipped_count ?? 0
        const changed = n + refreshed
        if (changed <= 0 && skipped <= 0) {
          trackRecipePackEvent('recipe_pack_apply_failed', {
            surface,
            ...ids,
            error_code: 'empty_apply',
          })
          applyFailureTracked = true
          showToast({ message: `No recipes were added from ${pack.name}.`, variant: 'error' })
          return
        }
        if (changed <= 0 && skipped > 0) {
          trackRecipePackEvent('recipe_pack_apply_failed', {
            surface,
            ...ids,
            error_code: 'all_skipped',
            skipped_count: skipped,
          })
          applyFailureTracked = true
          showToast({
            message: `${skipped} recipe${skipped === 1 ? '' : 's'} from ${pack.name} already with your recipes. Open Recipes to view them.`,
            variant: 'error',
          })
          notifySavedListsInvalidated()
          setLibraryReloadToken((t) => t + 1)
          return
        }
        trackRecipePackEvent('recipe_pack_apply_succeeded', {
          surface,
          ...ids,
          created_count: n,
          refreshed_count: refreshed,
          skipped_count: skipped,
        })
        let message = refreshed > 0 && n === 0
          ? `Updated ${refreshed} recipe${refreshed === 1 ? '' : 's'} with the latest directions.`
          : `Added ${n} recipe${n === 1 ? '' : 's'}.`
        if (refreshed > 0 && n > 0) {
          message += ` Updated ${refreshed} with the latest directions.`
        }
        if (skipped > 0) {
          message += ` ${skipped} already with your recipes.`
        }
        showToast({ message, variant: 'success' })
        notifySavedListsInvalidated()
        setLibraryReloadToken((t) => t + 1)
      } catch (err) {
        if (!applyFailureTracked) {
          trackRecipePackEvent('recipe_pack_apply_failed', {
            surface,
            ...ids,
            error_code: 'client_error',
          })
        }
        showToast({
          message: err?.message || 'Could not add recipe pack.',
          variant: 'error',
        })
      } finally {
        setPlanBusy(false)
      }
    },
    [showToast, plannerState, addedPackIds],
  )

  const ensureAssignedIngredientLines = useCallback(async () => {
    const plan = livePlanRef.current
    if (!plan?.weekStartDate) return
    if (!assignedNeedsIngredientLines(plan.assigned)) return
    if (ingredientFetchRef.current === plan.weekStartDate) return
    const weekStart = plan.weekStartDate
    ingredientFetchRef.current = weekStart
    setAssignedIngredientsLoading(true)
    setAssignedIngredientsError(false)
    try {
      const { lists, failed } = await hydrateSavedListSummariesWithItemsForAssigned(
        recipeListsRef.current,
        plan.assigned,
      )
      if (livePlanRef.current?.weekStartDate !== weekStart) return
      recipeListsRef.current = mergeRecipeListRows(recipeListsRef.current, lists)
      setRecipeLists(recipeListsRef.current)
      const current = livePlanRef.current
      const next = {
        ...current,
        assigned: enrichMealsWithRecipeMeta(current.assigned, recipeListsRef.current),
        suggestions: enrichMealsWithRecipeMeta(current.suggestions, recipeListsRef.current),
      }
      weekCacheRef.current.set(weekStart, next)
      livePlanRef.current = next
      setLivePlan(next)
      if (failed) setAssignedIngredientsError(true)
    } catch (err) {
      console.warn('Assigned ingredient hydration failed', err)
      if (livePlanRef.current?.weekStartDate === weekStart) {
        setAssignedIngredientsError(true)
      }
    } finally {
      if (ingredientFetchRef.current === weekStart) ingredientFetchRef.current = null
      setAssignedIngredientsLoading(false)
    }
  }, [])

  const retryAssignedIngredientLines = useCallback(() => {
    ingredientFetchRef.current = null
    setAssignedIngredientsError(false)
  }, [])

  const livePlannerApi = useMemo(() => {
    if (!useLivePlanner || !livePlan) return null
    return {
      planId: livePlan.planId,
      weekStartDate: livePlan.weekStartDate,
      generatedAt: livePlan.generatedAt || null,
      planMetadata: livePlan.planMetadata || {},
      assignedMeals: livePlan.assigned,
      suggestionMeals: livePlan.suggestions,
      calendarContext,
      stripDays,
      busy: planBusy || weekNavLoading,
      notice: planNotice,
      clearNotice: () => setPlanNotice(null),
      runNoticeAction: () => {
        if (planNotice?.actionKind === 'refresh_grocery') {
          setPlanNotice(null)
          void buildGroceryList({ openShop: false }).catch(() => {})
        }
      },
      pendingMoveConflict,
      resolveMoveConflict,
      cancelMoveConflict,
      generatePlan,
      assignToNextOpen,
      assignToDay,
      assignToStripDay,
      unassignMeal,
      swapRecipeInPlace,
      markMealCooked,
      markMealSkipped,
      setNightOff,
      removePlannerCandidate,
      buildGroceryList,
      assistant: {
        events: assistantEvents,
        isRunning: assistantBusy,
        pendingConfirmation: assistantPendingConfirmation,
        pendingClarification: assistantPendingClarification,
        activeDays: assistantActiveDays,
      },
      runAssistantCommand,
      runPlanWithCook,
      planCookFlow,
      mealPlanStructure: preferences?.meal_plan?.structure || null,
      clearAssistantConfirmation,
      clearAssistantClarification,
    }
  }, [
    useLivePlanner,
    livePlan,
    calendarContext,
    stripDays,
    planBusy,
    weekNavLoading,
    planNotice,
    pendingMoveConflict,
    resolveMoveConflict,
    cancelMoveConflict,
    generatePlan,
    assignToNextOpen,
    assignToDay,
    assignToStripDay,
    unassignMeal,
    swapRecipeInPlace,
    markMealCooked,
    markMealSkipped,
    setNightOff,
    removePlannerCandidate,
    buildGroceryList,
    assistantEvents,
    assistantBusy,
    assistantPendingConfirmation,
    assistantPendingClarification,
    assistantActiveDays,
    runAssistantCommand,
    runPlanWithCook,
    planCookFlow,
    preferences,
    clearAssistantConfirmation,
    clearAssistantClarification,
  ])

  return {
    plannerState,
    libraryCount,
    pantryCount,
    weekLabel,
    preferences,
    loading,
    error,
    livePlannerBanner,
    dismissLivePlannerBanner,
    starterPacks,
    addedPackIds,
    onApplyStarterPack,
    livePlannerApi,
    weekNav,
    ensureAssignedIngredientLines,
    retryAssignedIngredientLines,
    assignedIngredientsLoading,
    assignedIngredientsError,
    reloadPlanner: () => setLibraryReloadToken((value) => value + 1),
  }
}
